import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography,} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CentralCalls from '../centralCalls';

function toSafeName(employee) {
  return employee?.name || employee?.employee_name || employee?.employee || `Employee ${employee?.oid ?? ''}`;
}

const EmployeeLinks = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedLink, setCopiedLink] = useState('');

  const baseUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.location.origin;
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    setError('');

    try {
      const rows = await CentralCalls.fetchTableDataNoWhere('employee');
      const nextEmployees = (Array.isArray(rows) ? rows : [])
        .filter((row) => Number(row?.oid) > 0)
        .sort((a, b) => toSafeName(a).localeCompare(toSafeName(b)));
      setEmployees(nextEmployees);
    } catch (err) {
      console.error(err);
      setEmployees([]);
      setError('Failed to load employees.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const buildLink = (employeeOid) => `${baseUrl}/TeamForms/${employeeOid}`;

  const copyLink = async (link) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = link;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setCopiedLink(link);
    } catch (copyErr) {
      console.error(copyErr);
      setError('Could not copy link to clipboard.');
    }
  };

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', py: 2 }}>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Employee Links
            </Typography>
            <Typography variant="body2" sx={{ color: '#475b6d' }}>
              Team Forms links for each employee.
            </Typography>
          </Box>
          <Button onClick={loadEmployees} variant="outlined" disabled={loading} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {copiedLink ? <Alert severity="success" sx={{ mb: 2 }}>Copied: {copiedLink}</Alert> : null}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : employees.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#526270' }}>
            No employees found.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {employees.map((employee) => {
              const employeeOid = Number(employee?.oid);
              const link = buildLink(employeeOid);

              return (
                <Paper key={employeeOid} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack spacing={1}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {toSafeName(employee)}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        size="small"
                        value={link}
                        InputProps={{ readOnly: true }}
                      />
                      <Button
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copyLink(link)}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 125 }}
                      >
                        Copy Link
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeLinks;
